import {MouseEventHandler, useCallback} from 'react';
import {InView} from 'react-intersection-observer';
import {ON_ISSUE_TITLE_MAP} from '../../../common/constant';
import {t, tt} from '../../../common/getTranslations';
import {twMerge} from '../../../common/twMerge';
import {CategoryType, Maybe, Series} from '../../../graphql/generated/types';

import {Button} from '../../atoms/Button/Button';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {Image} from '../../atoms/Image/Image';
import {Text} from '../../atoms/Text/Text';
import {ThumbnailRatio} from '../../molecules/ThumbnailWithBadges/ThumbnailRatio.constant';
import {ThumbnailType} from '../../molecules/ThumbnailWithBadges/ThumbnailType.enum';

export interface Props {
  className?: string;
  title: string;
  subTitle?: string;
  episode?: string;
  hasLastReadHistory?: boolean;
  showAnchorButton?: boolean;
  hasUnreadSingle: Maybe<boolean>;
  relatedSeries: Maybe<Series>;
  onClickGoToRelatedSeries?: (e: React.MouseEvent, buttonText: string) => void;
  onClickTicketActionButton?: MouseEventHandler;
  onClickAnchor?: MouseEventHandler;
  onInViewRelativeButton?: () => void;
}

type TicketActionAreaType = 'onlyTicket' | 'relatedMinimum' | 'relatedExtended';

export function ContentHomeTicketActionArea({
  className,
  showAnchorButton,
  onClickGoToRelatedSeries,
  onClickTicketActionButton,
  onClickAnchor,
  onInViewRelativeButton,
  title,
  subTitle,
  episode,
  hasLastReadHistory,
  hasUnreadSingle,
  relatedSeries,
  ...props
}: Props) {
  const ticketActionAreaType: TicketActionAreaType =
    !hasLastReadHistory || !relatedSeries
      ? 'onlyTicket'
      : hasUnreadSingle
        ? 'relatedMinimum'
        : 'relatedExtended';

  const relatedSeriesButtonText =
    relatedSeries?.categoryType === CategoryType.Webtoon
      ? t('contenthome_go_webtoon_button')
      : t('contenthome_go_novel_button');

  const handleClickRelatedSeries = useCallback(
    (e: React.MouseEvent) => {
      onClickGoToRelatedSeries?.(e, relatedSeriesButtonText);
    },
    [onClickGoToRelatedSeries, relatedSeriesButtonText]
  );
  return (
    <div
      className={twMerge(
        'fixed inset-x-0 bottom-0 z-60 box-border flex h-70px w-full bg-theme-bg-base p-8px',
        className
      )}
    >
      {ticketActionAreaType !== 'onlyTicket' && (
        <InView
          disabled={!onInViewRelativeButton}
          onChange={inView => inView && onInViewRelativeButton?.()}
          className={twMerge(
            'box-border flex h-54px',
            ticketActionAreaType === 'relatedMinimum'
              ? 'mr-8px'
              : 'w-[50%] min-w-[50%] max-w-[50%] pr-4px'
          )}
        >
          <RelatedButton
            ticketActionAreaType={ticketActionAreaType}
            handleClickRelatedSeries={handleClickRelatedSeries}
            relatedSeries={relatedSeries}
            relatedSeriesButtonText={relatedSeriesButtonText}
          />
        </InView>
      )}
      <div
        className={twMerge(
          'box-border flex grow items-center',
          ticketActionAreaType === 'relatedExtended' &&
            'w-[50%] min-w-[50%] max-w-[50%] grow pl-4px'
        )}
        {...props}
      >
        <div
          className={twMerge(
            'flex h-full w-full',
            'bg-static-point-yellow text-static-mono-black',
            'rounded-12pxr'
          )}
        >
          <Button
            className={twMerge(
              'flex grow cursor-pointer flex-col justify-center overflow-hidden pl-12px pr-0',
              'space-y-1pxr',
              'bg-transparent!',
              ticketActionAreaType === 'relatedExtended'
                ? 'text-left'
                : 'items-center',
              !showAnchorButton && 'pr-12pxr'
            )}
            onClick={onClickTicketActionButton}
          >
            <Text className={twMerge('font-medium2-bold')}>{title}</Text>
            {subTitle && (
              <div
                className={twMerge(
                  'flex items-center',
                  'font-small2 text-static-mono-black',
                  !showAnchorButton && 'pr-4px'
                )}
              >
                <Text className="line-clamp-1 text-ellipsis break-all">
                  {subTitle}
                  {episode && <>&nbsp;</>}
                </Text>
                {episode && (
                  <Text className="inline-flex shrink-0 grow">{episode}</Text>
                )}
              </div>
            )}
          </Button>
          {showAnchorButton && (
            <div className="relative h-full shrink-0">
              <div
                className="absolute -top-8pxr left-0 h-70pxr w-64pxr cursor-pointer"
                onClick={onClickAnchor}
              />
              <Button className="flex h-full w-fit-content min-w-fit grow-0 cursor-pointer flex-col items-center justify-center bg-transparent! px-8pxr">
                <Icon
                  type={IconType.ContentHomeAnchorStatic}
                  className="mb-1pxr h-24px w-40px shrink-0"
                />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RelatedButton({
  ticketActionAreaType,
  handleClickRelatedSeries,
  relatedSeries,
  relatedSeriesButtonText,
}: {
  ticketActionAreaType: TicketActionAreaType;
  handleClickRelatedSeries: MouseEventHandler;
  relatedSeries: Props['relatedSeries'];
  relatedSeriesButtonText: string;
}) {
  return (
    <>
      {ticketActionAreaType === 'relatedMinimum' && (
        <div
          className="relative h-full cursor-pointer select-none"
          onClick={handleClickRelatedSeries}
        >
          <Image
            src={relatedSeries?.thumbnail}
            alt="동일작 보기"
            className="h-54px w-54px min-w-[54px]"
            imageClassName="object-top"
            ratio={ThumbnailRatio[ThumbnailType.Square]}
            roundPx={12}
            innerLine={true}
          />
          <div className="absolute left-0 top-0 h-full w-full rounded-12pxr bg-[rgb(var(--colors-static-mono-black)_/_40%)]"></div>
          <div className="absolute bottom-3px w-full text-center">
            <Text className="font-small2-bold text-static-mono-white">
              {relatedSeriesButtonText}
            </Text>
          </div>
        </div>
      )}
      {ticketActionAreaType === 'relatedExtended' && (
        <div
          className={twMerge(
            'flex h-full w-full cursor-pointer select-none items-center overflow-hidden rounded-12pxr border border-solid border-transparent bg-theme-solid-100 px-8px text-theme-solid-0'
          )}
          onClick={handleClickRelatedSeries}
        >
          <div className="flex grow flex-col px-4px">
            <Text className="font-medium2-bold">{relatedSeriesButtonText}</Text>
            {relatedSeries?.onIssue && (
              <Text className="font-small2">
                {tt(ON_ISSUE_TITLE_MAP[relatedSeries.onIssue])}
              </Text>
            )}
          </div>
          <Image
            src={relatedSeries?.thumbnail}
            alt="동일작 보기"
            className="h-34px w-34px"
            roundPx={8}
            innerLine={true}
            imageClassName="object-top"
            ratio={ThumbnailRatio[ThumbnailType.Square]}
          />
        </div>
      )}
    </>
  );
}
